import { useEffect, useState, useCallback } from "react";
import { Navigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { toast } from "sonner";
import { format } from "date-fns";
import { CalendarIcon, Lock, Unlock, Users, Star, UserSquare, Settings as SettingsIcon, XCircle, Inbox, CalendarPlus, MessageSquare } from "lucide-react";
import ClientDetail from "@/components/ClientDetail";
import TherapistSettings from "@/components/TherapistSettings";
import SlotRequests from "@/components/SlotRequests";
import OfferedSlots from "@/components/OfferedSlots";
import ChatPanel from "@/components/ChatPanel";

export default function TherapistPortal() {
  const { user, loading } = useAuth();
  const [me, setMe] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [date, setDate] = useState(new Date());
  const [avail, setAvail] = useState({ all_hours: [], booked_hours: [], blocked_hours: [], booked_with_names: [], working_day: true });
  const [busyKey, setBusyKey] = useState(null);
  const [tab, setTab] = useState("schedule");
  const [clients, setClients] = useState([]);
  const [activeClientId, setActiveClientId] = useState(null);
  const [pendingRequestCount, setPendingRequestCount] = useState(0);
  const [conversations, setConversations] = useState([]);
  const [activeConvId, setActiveConvId] = useState(null);

  const refreshConversations = useCallback(async () => {
    try {
      const { data } = await api.get("/conversations");
      setConversations(data);
      if (!activeConvId && data.length > 0) setActiveConvId(data[0].id);
    } catch { /* ignore */ }
  }, [activeConvId]);

  const refreshBookings = useCallback(async () => {
    try {
      const { data } = await api.get("/therapist/me/bookings");
      setBookings(data);
    } catch (e) { /* keep stale */ }
  }, []);

  const refreshClients = useCallback(async () => {
    try {
      const { data } = await api.get("/therapist/me/clients");
      setClients(data);
    } catch (e) { /* keep stale */ }
  }, []);

  const loadAvail = useCallback(async (d) => {
    const yyyy = format(d, "yyyy-MM-dd");
    try {
      const { data } = await api.get("/therapist/me/availability", { params: { date: yyyy } });
      setAvail(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not load availability");
    }
  }, []);

  useEffect(() => {
    if (loading || !user) return;
    if (user.role !== "therapist") return;
    api.get("/therapist/me").then(({ data }) => setMe(data)).catch(() => setMe(false));
    refreshBookings();
    refreshClients();
    refreshConversations();
  }, [loading, user, refreshBookings, refreshClients, refreshConversations]);

  useEffect(() => {
    if (loading || !user || user.role !== "therapist") return;
    loadAvail(date);
  }, [date, loadAvail, loading, user]);

  if (loading || user === null) return <div className="min-h-[60vh] flex items-center justify-center text-mindora-muted">loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "therapist") {
    return (
      <div className="max-w-2xl mx-auto px-6 py-32 text-center">
        <h1 className="font-serif text-4xl text-mindora-ink leading-tight">This portal is for clinicians.</h1>
        <p className="text-mindora-muted mt-4">Your account doesn&apos;t have therapist access. If you&apos;ve recently been approved, log out and back in.</p>
      </div>
    );
  }

  const toggleSlot = async (h, action) => {
    const [hh, mm] = h.split(":").map(Number);
    const slot = new Date(date); slot.setHours(hh, mm, 0, 0);
    setBusyKey(`${h}-${action}`);
    try {
      await api.post(`/therapist/me/${action}`, { slot: slot.toISOString(), reason: "" });
      toast.success(action === "block" ? `Blocked ${h}` : `Opened ${h}`);
      await loadAvail(date);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not update");
    } finally {
      setBusyKey(null);
    }
  };

  const cancelBooking = async (b) => {
    if (!window.confirm(`Cancel session with ${b.user_name || "this client"} on ${format(new Date(b.slot), "PPpp")}?`)) return;
    const reason = window.prompt("Optional note to the client (leave blank to skip):", "") || "";
    try {
      await api.post(`/bookings/${b.id}/cancel`, { reason });
      toast.success("Session cancelled — client notified by email.");
      await refreshBookings();
      await loadAvail(date);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not cancel");
    }
  };

  const upcoming = bookings.filter((b) => new Date(b.slot) > new Date() && b.status !== "cancelled");
  const cancelledOrPast = bookings.filter((b) => !(new Date(b.slot) > new Date() && b.status !== "cancelled"));
  const slotList = (avail.all_hours && avail.all_hours.length) ? avail.all_hours : [];

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20" data-testid="page-therapist-portal">
      <div className="text-xs tracking-[0.25em] uppercase text-mindora-red mb-4">
        <span className="editorial-rule" />Therapist portal
      </div>
      <h1 className="font-serif text-5xl lg:text-6xl text-mindora-ink leading-tight">
        Welcome, <em className="italic text-mindora-red">{me?.name?.split(" ").slice(-2).join(" ") || "Doctor"}.</em>
      </h1>
      <p className="mt-4 text-mindora-muted max-w-2xl">
        Manage your availability and see who&apos;s booked with you. Block a slot if you can&apos;t make it; open it back up when you can.
      </p>

      {/* Stats / profile */}
      {me && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-10" data-testid="portal-stats">
          <Stat icon={<Users size={14} />} label="Upcoming sessions" value={upcoming.length} />
          <Stat icon={<Star size={14} />} label="Rating" value={me.rating} />
          <Stat icon={<CalendarIcon size={14} />} label="Session price" value={`₹${(me.session_price || 0).toLocaleString("en-IN")}`} />
        </div>
      )}

      {/* Tabs */}
      <div className="mt-14 flex items-center gap-1 border-b border-mindora-line overflow-x-auto" data-testid="portal-tabs">
        {[
          { key: "schedule", label: "Schedule & bookings", icon: <CalendarIcon size={13} /> },
          { key: "offered", label: "Offered times", icon: <CalendarPlus size={13} /> },
          { key: "messages", label: `Messages${conversations.reduce((s, c) => s + (c.unread_count || 0), 0) ? ` (${conversations.reduce((s, c) => s + (c.unread_count || 0), 0)})` : ""}`, icon: <MessageSquare size={13} /> },
          { key: "requests", label: `Requests${pendingRequestCount ? ` (${pendingRequestCount})` : ""}`, icon: <Inbox size={13} /> },
          { key: "clients", label: `Clients (${clients.length})`, icon: <UserSquare size={13} /> },
          { key: "settings", label: "Settings", icon: <SettingsIcon size={13} /> },
        ].map((t) => (
          <button
            key={t.key}
            onClick={() => { setTab(t.key); setActiveClientId(null); }}
            data-testid={`portal-tab-${t.key}`}
            className={`relative -mb-px inline-flex items-center gap-2 px-4 py-3 text-sm tracking-wide whitespace-nowrap transition-colors ${
              tab === t.key
                ? "text-mindora-red border-b-2 border-mindora-red"
                : "text-mindora-muted hover:text-mindora-ink border-b-2 border-transparent"
            }`}
          >
            {t.icon} {t.label}
          </button>
        ))}
      </div>

      {tab === "clients" ? (
        <div className="mt-10" data-testid="portal-clients-view">
          {activeClientId ? (
            <ClientDetail clientId={activeClientId} onBack={() => setActiveClientId(null)} />
          ) : (
            <ClientsList clients={clients} onOpen={setActiveClientId} />
          )}
        </div>
      ) : tab === "offered" ? (
        <div className="mt-10" data-testid="portal-offered-view">
          <OfferedSlots />
        </div>
      ) : tab === "messages" ? (
        <div className="mt-10" data-testid="portal-messages-view">
          <ChatPanel
            conversations={conversations}
            activeId={activeConvId}
            onSelectConversation={setActiveConvId}
            onConversationsChanged={refreshConversations}
            viewerRole="therapist"
          />
        </div>
      ) : tab === "requests" ? (
        <div className="mt-10" data-testid="portal-requests-view">
          <SlotRequests />
        </div>
      ) : tab === "settings" ? (
        <div className="mt-10" data-testid="portal-settings-view">
          <TherapistSettings onSaved={() => { loadAvail(date); }} />
        </div>
      ) : (
      <div className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-10">
        {/* Calendar + slot manager */}
        <div className="lg:col-span-7">
          <h2 className="font-serif text-3xl text-mindora-ink">Your day</h2>
          <p className="text-sm text-mindora-muted mt-1">Pick a date to manage time slots.</p>

          <div className="mt-5 rounded-2xl border border-mindora-line bg-mindora-panel p-3 inline-block">
            <Calendar
              mode="single"
              selected={date}
              onSelect={(d) => d && setDate(d)}
              disabled={(d) => d < new Date(new Date().setHours(0,0,0,0))}
              data-testid="portal-calendar"
            />
          </div>

          <div className="mt-8">
            <div className="flex flex-wrap items-baseline justify-between gap-3 mb-3">
              <div className="text-xs uppercase tracking-[0.2em] text-mindora-ink/80">
                Slots for {format(date, "PPPP")}
              </div>
              {!avail.working_day && (
                <span className="text-xs text-mindora-muted italic">Not a working day · adjust in Settings</span>
              )}
            </div>
            {slotList.length === 0 ? (
              <div className="rounded-2xl border border-mindora-line bg-mindora-panel p-6 text-mindora-muted">
                {avail.working_day ? "No slots configured. Check your Settings." : "Off today — no sessions on this weekday."}
              </div>
            ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="portal-slots">
              {slotList.map((h) => {
                const isBooked = avail.booked_hours?.includes(h);
                const isBlocked = avail.blocked_hours?.includes(h);
                const isWorking = busyKey?.startsWith(`${h}-`);
                const booking = avail.booked_with_names?.find((x) => x.hour === h);
                return (
                  <div
                    key={h}
                    data-testid={`portal-slot-${h}`}
                    className={`rounded-2xl border p-4 transition-all ${
                      isBooked
                        ? "bg-mindora-red/10 border-mindora-red"
                        : isBlocked
                          ? "bg-mindora-panel border-mindora-line opacity-70"
                          : "bg-mindora-bg border-mindora-line hover:border-mindora-red"
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-serif text-2xl text-mindora-ink">{h}</span>
                      <span className={`text-[10px] uppercase tracking-[0.2em] ${
                        isBooked ? "text-mindora-red" : isBlocked ? "text-mindora-muted" : "text-mindora-ink/60"
                      }`}>
                        {isBooked ? "booked" : isBlocked ? "blocked" : "open"}
                      </span>
                    </div>

                    {isBooked && (
                      <>
                        <div className="mt-2 text-xs text-mindora-ink/80 truncate" data-testid={`portal-slot-${h}-booked-by`}>
                          with {booking?.user_name || "a client"}
                        </div>
                        {booking && (
                          <button
                            onClick={() => cancelBooking({ id: booking.id, slot: booking.slot, user_name: booking.user_name })}
                            data-testid={`portal-slot-${h}-cancel`}
                            className="mt-2 w-full inline-flex items-center justify-center gap-1.5 rounded-full border border-mindora-line text-mindora-muted hover:border-mindora-red hover:text-mindora-red px-3 py-1 text-[11px] tracking-wide"
                          >
                            <XCircle size={10} /> Cancel this session
                          </button>
                        )}
                      </>
                    )}

                    {!isBooked && (
                      <button
                        onClick={() => toggleSlot(h, isBlocked ? "unblock" : "block")}
                        disabled={isWorking}
                        data-testid={`portal-slot-${h}-toggle`}
                        className={`mt-3 w-full inline-flex items-center justify-center gap-1.5 rounded-full px-3 py-1.5 text-xs tracking-wide transition-colors ${
                          isBlocked
                            ? "bg-mindora-ink text-mindora-bg hover:bg-mindora-red"
                            : "border border-mindora-line text-mindora-ink hover:border-mindora-red hover:text-mindora-red"
                        }`}
                      >
                        {isBlocked ? <><Unlock size={11} /> Open it back up</> : <><Lock size={11} /> Block this slot</>}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
            )}
            <p className="text-xs text-mindora-muted mt-4 italic">
              Booked slots are visible to you only — your clients see them as unavailable. Blocking removes a slot from public availability.
            </p>
          </div>
        </div>

        {/* Bookings */}
        <div className="lg:col-span-5">
          <h2 className="font-serif text-3xl text-mindora-ink">Upcoming sessions</h2>
          <p className="text-sm text-mindora-muted mt-1">{upcoming.length} {upcoming.length === 1 ? "session" : "sessions"} ahead.</p>
          <div className="mt-5 space-y-3" data-testid="portal-bookings">
            {upcoming.length === 0 && (
              <div className="rounded-2xl border border-mindora-line bg-mindora-panel p-6 text-mindora-muted italic">
                No upcoming sessions. When clients book you, they&apos;ll appear here.
              </div>
            )}
            {upcoming.map((b) => (
              <div key={b.id} className="rounded-2xl border border-mindora-line bg-mindora-panel p-5" data-testid={`portal-booking-${b.id}`}>
                <div className="flex items-center justify-between">
                  <span className="text-[10px] uppercase tracking-[0.2em] text-mindora-red">{b.status}</span>
                  <span className="text-xs text-mindora-muted">{format(new Date(b.slot), "PPpp")}</span>
                </div>
                <div className="font-serif text-xl text-mindora-ink mt-2 leading-tight">{b.user_name || "Anonymous"}</div>
                <div className="text-xs text-mindora-muted mt-0.5">{b.user_email}</div>
                {b.note && <div className="mt-3 text-sm text-mindora-ink/80 italic">&ldquo;{b.note}&rdquo;</div>}
                <div className="mt-4 pt-3 border-t border-mindora-line flex items-center justify-between gap-3">
                  <button
                    onClick={() => { setActiveClientId(b.user_id); setTab("clients"); }}
                    data-testid={`portal-booking-${b.id}-open-client`}
                    className="text-xs text-mindora-red hover:underline"
                  >
                    Open client notes →
                  </button>
                  <button
                    onClick={() => cancelBooking(b)}
                    data-testid={`portal-booking-${b.id}-cancel`}
                    className="text-xs text-mindora-muted hover:text-mindora-red inline-flex items-center gap-1"
                  >
                    <XCircle size={11} /> Cancel
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      )}
    </div>
  );
}

function ClientsList({ clients, onOpen }) {
  if (clients.length === 0) {
    return (
      <div className="rounded-3xl border border-mindora-line bg-mindora-panel p-10 text-center" data-testid="clients-empty">
        <p className="font-serif text-2xl text-mindora-ink">No clients yet.</p>
        <p className="text-mindora-muted mt-2">When someone books a session with you, they&apos;ll show up here with space for your private notes.</p>
      </div>
    );
  }
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="clients-list">
      {clients.map((c) => (
        <button
          key={c.client_id}
          onClick={() => onOpen(c.client_id)}
          data-testid={`client-card-${c.client_id}`}
          className="text-left rounded-3xl border border-mindora-line bg-mindora-panel p-6 shadow-clay hover:shadow-clayHover hover:-translate-y-1 transition-all"
        >
          <div className="font-serif text-2xl text-mindora-ink leading-tight">{c.name || "Client"}</div>
          <div className="text-xs text-mindora-muted mt-1">{c.email}</div>
          <div className="mt-4 grid grid-cols-2 gap-3 text-xs">
            <div>
              <div className="uppercase tracking-[0.18em] text-mindora-red text-[10px]">Sessions</div>
              <div className="font-serif text-xl text-mindora-ink mt-0.5">{c.sessions_count}</div>
            </div>
            <div>
              <div className="uppercase tracking-[0.18em] text-mindora-red text-[10px]">Next</div>
              <div className="text-mindora-ink mt-0.5">{c.next_session_at ? new Date(c.next_session_at).toLocaleDateString() : "—"}</div>
            </div>
          </div>
          <div className="mt-5 text-xs text-mindora-red tracking-wide">Open notes →</div>
        </button>
      ))}
    </div>
  );
}

function Stat({ icon, label, value }) {
  return (
    <div className="rounded-2xl border border-mindora-line bg-mindora-panel p-5">
      <div className="flex items-center gap-2 text-mindora-red text-xs uppercase tracking-[0.2em]">
        {icon} {label}
      </div>
      <div className="font-serif text-4xl text-mindora-ink mt-2">{value}</div>
    </div>
  );
}
